import React, { useEffect, useState } from 'react';
import { Box, Card, CardBody, CardHeader, Heading, Spinner, Center, Text } from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import { useNavigateToList } from '@/hooks/useNavigateToList';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { TruckMaintenanceForm } from '@/components/forms/TruckMaintenanceForm';
import { apiClient } from '@/lib/api/client';

export default function AddMaintenancePage() {
    const { id } = useParams<{ id: string }>();
    const [truckData, setTruckData] = useState<{ plate_number: string } | null>(null);
    const historyFallback = (() => {
        const q = new URLSearchParams();
        if (truckData?.plate_number) {
            q.set('plate_number', String(truckData.plate_number));
        }
        const qs = q.toString();
        return id
            ? `/fleet-management/maintenance-history/${id}${qs ? `?${qs}` : ''}`
            : '/fleet-management/trucks';
    })();
    const navigateToList = useNavigateToList(historyFallback);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchTruck = async () => {
            try {
                const response = await apiClient.get(`/trucks/get-truck-by-id/${id}`);
                // Extract data regardless of wrapper structure
                const data = response.data?.data || response.data;
                setTruckData(data);
            } catch (error) {
                console.error("Error fetching truck details:", error);
            } finally {
                setIsLoading(false);
            }
        };
        if (id) fetchTruck();
    }, [id]);

    return (
        <AdminLayout>
            {/* 1. Outer container: h="full", overflow="hidden", and flex column */}
            <Box p={4} h="full" overflow="hidden" display="flex" flexDirection="column">
                
                {/* 2. Card: h="full" and flex column */}
                <Card h="full" display="flex" flexDirection="column" variant="outline" boxShadow="sm" borderRadius="lg">
                    
                    {/* 3. CardHeader: flexShrink={0} ensures it stays pinned at the top */}
                    <CardHeader borderBottom="1px solid" borderColor="gray.100" py={4} flexShrink={0}>
                        <Heading size="md" color="blue.600">Add Maintenance Record</Heading>
                        {truckData && (
                            <Text fontSize="sm" color="gray.500" mt={1}>
                                For Truck: <strong>{truckData.plate_number}</strong>
                            </Text>
                        )}
                    </CardHeader>
                    
                    {/* 4. CardBody: overflowY="auto" and flex="1" enables scrolling here only */}
                    <CardBody overflowY="auto" flex="1" minH={0} py={6}>
                        {isLoading ? (
                            <Center h="200px">
                                <Spinner color="blue.500" size="xl" thickness="4px" />
                            </Center>
                        ) : truckData ? (
                            <TruckMaintenanceForm 
                                truckId={Number(id)} 
                                plateNumber={truckData.plate_number} 
                                onSuccess={() => navigateToList()}
                                onCancel={() => navigateToList()}
                            />
                        ) : (
                            <Center h="100px">
                                <Text color="red.500">Could not retrieve truck details.</Text>
                            </Center>
                        )}
                    </CardBody>
                </Card>
            </Box>
        </AdminLayout>
    );
}
